import { createApi } from '@reduxjs/toolkit/query/react';
import type { SavedSearch } from '@/components/search/saved-searches.component';
import { parseServerDate } from '@/lib/parse-server-date';
import { baseQueryWithAuth } from '@/store/base-query';
import { migrateSavedSearches } from '@/utils/legacy-search-migration';

/** The API's tab vocabulary (the route's ``companies`` is ``company`` here). */
export type FinderSearchTab = 'people' | 'company';

export interface FinderSearchBody {
  tab: FinderSearchTab;
  filters: Record<string, unknown>;
}

interface FinderSearchRow extends FinderSearchBody {
  id: string;
  code: string;
  name: string;
  created_by: string;
  /** Naive UTC. */
  created_at: string;
}

/** A list row from ``?summary=true``: the filter count, not the filters. */
type FinderSearchSummaryRow = Omit<FinderSearchRow, 'filters'> & {
  filter_count: number;
};

function toSavedSearch(row: FinderSearchRow): SavedSearch {
  // Today's SavedSearch shape, through the same migrator the browser's saved
  // searches load through (it fills the dropped defaults back in).
  return migrateSavedSearches([
    {
      id: row.id,
      name: row.name,
      searchType: row.tab,
      filters: row.filters,
      createdAt:
        parseServerDate(row.created_at)?.toISOString() ?? row.created_at,
    },
  ])[0];
}

export interface FinderSearchLink extends FinderSearchBody {
  /** The sharer's workspace-only exclusions (their lists, CRM owners and
   *  fields) were taken out because the viewer is in another workspace. */
  workspace_exclusions_removed: boolean;
}

const headers = (orgId: string) => ({ 'X-Cleanlist-Expected-Org': orgId });
const savedTags = (orgId: string) => [
  { type: 'FinderSavedSearches' as const, id: orgId },
];

/** Team saved searches and share links for the Finder
 *  (``finder_shared_searches_v1``). Every request names the active
 *  organization, like ``searchProfilesApi``. */
export const finderSearchesApi = createApi({
  reducerPath: 'finderSearchesApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['FinderSavedSearches'],
  endpoints: (builder) => ({
    // Names only: a bulk-pasted search carries tens of KB of URLs, and the
    // lists show a name, a date and a count. ``getSavedFinderSearch`` brings
    // one search's filters when it is applied or shared.
    getSavedFinderSearches: builder.query<SavedSearch[], string>({
      query: (orgId) => ({
        url: '/finder-searches',
        params: { summary: true },
        headers: headers(orgId),
      }),
      transformResponse: ({
        searches,
      }: {
        searches: Array<FinderSearchSummaryRow | FinderSearchRow>;
      }) =>
        searches.map((row) =>
          // An API from before ``summary`` still sends the filters.
          'filters' in row
            ? toSavedSearch(row)
            : {
                id: row.id,
                name: row.name,
                searchType: row.tab,
                filters: {},
                filterCount: row.filter_count,
                createdAt:
                  parseServerDate(row.created_at)?.toISOString() ??
                  row.created_at,
              }
        ),
      providesTags: (_result, _error, orgId) => savedTags(orgId),
    }),
    getSavedFinderSearch: builder.query<
      SavedSearch,
      { orgId: string; id: string }
    >({
      query: ({ orgId, id }) => ({
        url: `/finder-searches/${encodeURIComponent(id)}`,
        headers: headers(orgId),
      }),
      transformResponse: toSavedSearch,
    }),
    saveFinderSearch: builder.mutation<
      FinderSearchRow,
      FinderSearchBody & { orgId: string; name: string }
    >({
      query: ({ orgId, ...body }) => ({
        url: '/finder-searches',
        method: 'POST',
        headers: headers(orgId),
        body,
      }),
      invalidatesTags: (_result, _error, { orgId }) => savedTags(orgId),
    }),
    deleteFinderSearch: builder.mutation<void, { orgId: string; id: string }>({
      query: ({ orgId, id }) => ({
        url: `/finder-searches/${id}`,
        method: 'DELETE',
        headers: headers(orgId),
      }),
      // Drop the row at once. Left on screen until the refetch, a second
      // click sent another DELETE for a search already gone and toasted its
      // 404. A 404 means it is gone, so only another failure puts it back:
      // that one row, by id, so an overlapping delete's removal stays.
      async onQueryStarted({ orgId, id }, { dispatch, queryFulfilled }) {
        let removed: { row: SavedSearch; index: number } | undefined;
        dispatch(
          finderSearchesApi.util.updateQueryData(
            'getSavedFinderSearches',
            orgId,
            (rows) => {
              const index = rows.findIndex((row) => row.id === id);
              if (index === -1) return;
              removed = { row: rows[index], index };
              rows.splice(index, 1);
            }
          )
        );
        try {
          await queryFulfilled;
        } catch (failure) {
          const status = (failure as { error?: { status?: unknown } }).error
            ?.status;
          if (status === 404 || !removed) return;
          const { row, index } = removed;
          dispatch(
            finderSearchesApi.util.updateQueryData(
              'getSavedFinderSearches',
              orgId,
              (rows) => {
                if (rows.some((r) => r.id === row.id)) return;
                rows.splice(Math.min(index, rows.length), 0, row);
              }
            )
          );
        }
      },
      invalidatesTags: (_result, _error, { orgId }) => savedTags(orgId),
    }),
    createFinderSearchLink: builder.mutation<
      { code: string },
      FinderSearchBody & { orgId: string }
    >({
      query: ({ orgId, ...body }) => ({
        url: '/finder-searches/links',
        method: 'POST',
        headers: headers(orgId),
        body,
      }),
    }),
    // A link opens in any workspace: the response is the search alone.
    getFinderSearchLink: builder.query<
      FinderSearchLink,
      { orgId: string; code: string }
    >({
      query: ({ orgId, code }) => ({
        url: `/finder-searches/links/${encodeURIComponent(code)}`,
        headers: headers(orgId),
      }),
    }),
  }),
});

export const {
  useGetSavedFinderSearchesQuery,
  useLazyGetSavedFinderSearchQuery,
  useSaveFinderSearchMutation,
  useDeleteFinderSearchMutation,
  useCreateFinderSearchLinkMutation,
  useGetFinderSearchLinkQuery,
} = finderSearchesApi;
